import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { useAppDispatch } from '../../../app/hooks';
import {
  fetchRevertPreview,
  revertSeasonRateCopy,
  revertSeasonRateDateChange,
  RevertPreviewDto,
  RevertResultDto,
} from '../../../features/seasonRateTools/rateToolsSlice';
import { addStatusToast } from '../StatusToast/StatusToastSlice';
import { Loader } from '../Loader';
import { SEASON_RATE_BATCH_OPERATION_TYPE } from '../../constants';

export interface RevertConfirmationProps {
  ShowModal: boolean;
  HideModal: () => void;
  BatchUpdateDetailId: string;
  OperationType: SEASON_RATE_BATCH_OPERATION_TYPE;
  OnReverted: (result: RevertResultDto) => void;
}

const RevertConfirmation = ({
  ShowModal,
  HideModal,
  BatchUpdateDetailId,
  OperationType,
  OnReverted,
}: RevertConfirmationProps) => {
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<RevertPreviewDto | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [force, setForce] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  useEffect(() => {
    if (!ShowModal) return;

    setPreview(null);
    setPreviewError(null);
    setForce(false);

    dispatch(fetchRevertPreview(BatchUpdateDetailId))
      .unwrap()
      .then((result: RevertPreviewDto | null) => {
        if (result === null) {
          setPreviewError('Palautettavaa operaatiota ei löytynyt.');
        } else {
          setPreview(result);
        }
      })
      .catch(() => {
        setPreviewError('Palautuksen esikatselun lataus epäonnistui.');
      });
  }, [ShowModal, BatchUpdateDetailId, dispatch]);

  const handleConfirmRevert = async () => {
    setIsReverting(true);
    try {
      const thunk =
        OperationType === SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying
          ? revertSeasonRateCopy
          : revertSeasonRateDateChange;

      const response = await dispatch(
        thunk({ batchUpdateDetailId: BatchUpdateDetailId, force }),
      ).unwrap();

      if (response?.isSuccess === false) {
        dispatch(
          addStatusToast({
            Message: response.message ?? 'Palautus epäonnistui.',
            IsFailed: true,
          }),
        );
        setIsReverting(false);
        return;
      }

      const { result } = response as { result: RevertResultDto };

      dispatch(
        addStatusToast({
          Message:
            'Palautus käynnistetty taustalla. Lista päivittyy kun se on valmis.',
          IsFailed: false,
        }),
      );

      OnReverted(result);
      HideModal();
    } catch {
      dispatch(
        addStatusToast({
          Message: 'Palautus epäonnistui. Yritä uudelleen.',
          IsFailed: true,
        }),
      );
      setIsReverting(false);
    }
  };

  const renderBody = () => {
    if (previewError) {
      return <Alert variant="danger">{previewError}</Alert>;
    }

    if (preview === null) {
      return <Loader IsLoading />;
    }

    if (!preview.isRevertible) {
      return (
        <Alert variant="info">
          Tämä operaatio on jo palautettu eikä sitä voida palauttaa uudelleen.
        </Alert>
      );
    }

    const cleanCount = preview.totalAffectedCount - preview.conflictCount;

    return (
      <>
        <p>
          Operaatio koskee yhteensä{' '}
          <strong>{preview.totalAffectedCount}</strong> riviä.
        </p>
        <p>
          Palautetaan <strong>{cleanCount}</strong> rivi
          {cleanCount === 1 ? '' : 'ä'}.
        </p>

        {preview.conflictCount > 0 && (
          <Alert variant="warning">
            <p className="mb-2">
              <strong>{preview.conflictCount}</strong> rivi
              {preview.conflictCount === 1 ? '' : 'ä'} on muuttunut tämän
              operaation jälkeen. Niitä ei oletuksena palauteta.
            </p>
            <Table size="sm" striped className="mb-2">
              <thead>
                <tr>
                  <th>Kohteen numero</th>
                  <th>Kohteen nimi</th>
                  <th>Aikaväli</th>
                  <th>Syy</th>
                </tr>
              </thead>
              <tbody>
                {preview.conflicts.map((conflict, idx) => (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${conflict.accommodationNumber ?? 'x'}-${idx}`}
                  >
                    <td>{conflict.accommodationNumber ?? '-'}</td>
                    <td>{conflict.accommodationName ?? '-'}</td>
                    <td>
                      {format(new Date(conflict.seasonStartDate), 'dd.MM.yyyy')}
                      {' – '}
                      {format(new Date(conflict.seasonEndDate), 'dd.MM.yyyy')}
                    </td>
                    <td>{conflict.conflictReason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Form.Check
              id="force-revert"
              type="checkbox"
              label="Pakota palautus, mukaan lukien konfliktissa olevat rivit"
              checked={force}
              onChange={e => setForce(e.target.checked)}
            />
          </Alert>
        )}
      </>
    );
  };

  const canConfirm =
    preview !== null && preview.isRevertible && previewError === null;

  return (
    <Modal show={ShowModal} onHide={HideModal} centered size="lg">
      <Modal.Header>
        <Modal.Title>Palautuksen vahvistaminen</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-24 py-32 px-lg-40">{renderBody()}</Modal.Body>
      <Modal.Footer className="border-0 bg-light p-24">
        <Button
          variant="link"
          onClick={HideModal}
          className="px-32"
          disabled={isReverting}
        >
          Peruuta ja sulje
        </Button>
        <Button
          variant="warning"
          onClick={handleConfirmRevert}
          className="px-32"
          disabled={!canConfirm || isReverting}
        >
          {isReverting ? 'Palautetaan…' : 'Palauta'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RevertConfirmation;
export { RevertConfirmation };
